import { Fragment, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { PanelLeft, Search } from "lucide-react";
import { ProjectSidebar } from "@/components/projects/project-sidebar";
import { useSearchOverlay } from "@/components/layout/search-overlay-context";

export type Crumb = { label: string; to?: string };

/**
 * Shared chrome for the project interior (Task 5/6): fixed top bar
 * (collapse toggle + breadcrumb + global search + layout toggle) and the
 * independently-scrolling left sidebar. Pages render their own toolbar + scroll
 * region as `children` inside the content column. Used by All Shares + Edit
 * Share so the sidebar persists across the project interior.
 */
export function ProjectShell({
  projectId,
  projectsBasePath,
  crumbs,
  crumbTrailing,
  children,
}: {
  projectId: string | undefined;
  projectsBasePath: string;
  crumbs: Crumb[];
  /** Optional element rendered right after the breadcrumb (e.g. a Restricted lock badge). */
  crumbTrailing?: ReactNode;
  children: ReactNode;
}) {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const { openWith } = useSearchOverlay();

  return (
    <div className="flex h-[100dvh] flex-col bg-background">
      <header className="flex shrink-0 items-center gap-3 border-b border-border px-3 py-2 md:px-4">
        <button
          type="button"
          aria-label={sidebarOpen ? "Hide sidebar" : "Show sidebar"}
          onClick={() => setSidebarOpen((v) => !v)}
          className="grid h-8 w-8 shrink-0 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
        >
          <PanelLeft className="h-4 w-4" strokeWidth={1.75} />
        </button>

        <nav className="flex min-w-0 items-center gap-1.5 text-sm">
          <Link
            to={projectsBasePath}
            className="shrink-0 text-muted-foreground transition-colors hover:text-foreground"
          >
            All Projects
          </Link>
          {crumbs.map((c, i) => (
            <Fragment key={i}>
              <span className="shrink-0 text-muted-foreground">/</span>
              {c.to ? (
                <Link
                  to={c.to}
                  className="shrink-0 truncate text-muted-foreground transition-colors hover:text-foreground"
                >
                  {c.label}
                </Link>
              ) : (
                <span className="truncate font-display text-base font-bold tracking-tight text-foreground">
                  {c.label}
                </span>
              )}
            </Fragment>
          ))}
          {crumbTrailing}
        </nav>

        <div className="flex-1" />

        <button
          type="button"
          aria-label="Search all projects"
          onClick={() => openWith({ kind: "workspace" })}
          className="hidden h-8 w-full max-w-xs items-center gap-2 rounded-full border border-border bg-secondary/50 px-3 text-sm text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground md:flex"
        >
          <Search className="h-4 w-4 shrink-0" strokeWidth={2} />
          <span className="flex-1 text-left">Search…</span>
        </button>
        <button
          type="button"
          aria-label={sidebarOpen ? "Hide left panel" : "Show left panel"}
          onClick={() => setSidebarOpen((v) => !v)}
          className="grid h-8 w-8 shrink-0 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
        >
          <PanelLeft className="h-4 w-4" strokeWidth={1.75} />
        </button>
      </header>

      <div className="flex min-h-0 flex-1">
        {sidebarOpen && (
          <ProjectSidebar
            projectId={projectId}
            projectsBasePath={projectsBasePath}
            foldersBasePath={`${projectsBasePath}/${projectId}/folders`}
            sharesBasePath={`${projectsBasePath}/${projectId}/shares`}
          />
        )}
        <div className="flex min-w-0 flex-1 flex-col">{children}</div>
      </div>
    </div>
  );
}
